import { useEffect, useState } from 'react';
import { api, openAuthed, money } from '../api.js';
import { ReportHead, Money, Empty } from '../components/Shell.jsx';

const TYPE_LABEL = {
  sales_invoice: 'Tax invoice', sales_credit_note: 'Credit note',
  purchase_invoice: 'Supplier invoice', purchase_credit_note: 'Supplier credit note',
};

/** Everything raised for this client, with the tax invoice and the void action. */
export function Documents({ entity }) {
  const [docs, setDocs] = useState(null);
  const [filter, setFilter] = useState('');
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);
  const [blocked, setBlocked] = useState(null);
  const [confirming, setConfirming] = useState(null);

  const load = () => api.get(`/api/entities/${entity.id}/documents?limit=200${filter ? `&type=${filter}` : ''}`)
    .then((d) => setDocs(d.documents)).catch((e) => setErr(e.message));

  useEffect(() => { if (entity?.id) load(); }, [entity?.id, filter]);

  const openPdf = async (doc, proforma = false) => {
    setErr(null); setMsg(null); setBlocked(null);
    try {
      await openAuthed(`/api/entities/${entity.id}/documents/${doc.id}/pdf${proforma ? '?proforma=true' : ''}`);
    } catch (e) {
      // The s.20 gate answers 422 with the exact list of missing fields.
      if (e.code === 'NOT_S20_COMPLIANT') setBlocked({ doc, ...e.payload });
      else setErr(e.message);
    }
  };

  const voidDoc = async (doc) => {
    setErr(null); setMsg(null); setConfirming(null);
    try {
      const r = await api.post(`/api/entities/${entity.id}/documents/${doc.id}/void`, {});
      setMsg(`${r.voided} voided — reversed by journal entry ${r.reversalEntryNo}.`);
      load();
    } catch (e) { setErr(e.message); }
  };

  if (!docs) return <div className="empty">Loading…</div>;

  return (
    <>
      <ReportHead entity={entity} title="Documents"
                  sub="Invoices and credit notes raised for this client." />
      {err && <div className="error">{err}</div>}
      {msg && <div className="ok">{msg}</div>}

      {blocked && (
        <div className="error">
          <strong>{blocked.doc.number} cannot be issued as a tax invoice yet.</strong>
          <div style={{ margin: '8px 0' }}>{blocked.detail}</div>
          <ul style={{ margin: '0 0 10px 18px', padding: 0 }}>
            {blocked.missing.map((m) => (
              <li key={m.field}>{m.field} <span style={{ opacity: 0.75 }}>({m.why}) — {m.fix}</span></li>
            ))}
          </ul>
          <button className="btn btn--quiet" onClick={() => openPdf(blocked.doc, true)}>
            Download a proforma instead
          </button>
        </div>
      )}

      <div className="controls">
        <label className="field"><span>Show</span>
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="">Everything</option>
            {Object.entries(TYPE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select></label>
      </div>

      {docs.length === 0 ? (
        <Empty><p>Nothing raised yet.</p><p>Raise one under Tax invoice.</p></Empty>
      ) : (
        <div className="scroll-x">
          <table className="ledger">
            <thead>
              <tr>
                <th style={{ width: 90 }}>Number</th><th style={{ width: 100 }}>Date</th>
                <th>Contact</th><th>Type</th>
                <th className="num">Total</th><th className="num">Outstanding</th>
                <th style={{ width: 190 }} />
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => (
                <tr key={d.id}>
                  <td className="code figure">{d.number}</td>
                  <td className="figure">{d.document_date}</td>
                  <td>{d.counterparty_name}</td>
                  <td style={{ fontSize: 12, color: 'var(--ink-soft)' }}>{TYPE_LABEL[d.type]}</td>
                  <td className="num"><Money value={d.gross} /></td>
                  <td className="num"><Money value={d.outstanding} /></td>
                  <td className="num">
                    <button className="icon-btn" onClick={() => openPdf(d)}>PDF</button>{' '}
                    {confirming === d.id ? (
                      <>
                        <button className="icon-btn" style={{ borderColor: 'var(--unbalanced)', color: 'var(--unbalanced)' }}
                                onClick={() => voidDoc(d)}>Confirm void</button>{' '}
                        <button className="icon-btn" onClick={() => setConfirming(null)}>No</button>
                      </>
                    ) : (
                      <button className="icon-btn" onClick={() => setConfirming(d.id)}>Void</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="note">
        Voiding reverses the document's journal entry and leaves both visible. A document that has
        been paid or part-paid cannot be voided — issue a credit note instead, which is also what
        the customer expects if they have already seen the invoice.
      </div>
    </>
  );
}
